/**
 * Phase 8 chunk 4 — pure-math support for the mobile detail-panel
 * bottom sheet. No DOM access; the {@link PanelGesture} controller
 * imports these constants/helpers and applies them to a live element.
 *
 * Snap points are expressed as `translateY` percentages of the panel's
 * own height — the panel is anchored at `bottom: 0; height: 100%`
 * inside `.globe-area`, so translateY(75%) leaves 25% of the panel
 * visible at the bottom of the viewport (the "peek" state).
 */

export const SNAP_FULL = 25;  // translateY(25%) → 75% of panel visible (≈ 75 vh)
export const SNAP_MID = 50;   // translateY(50%) → 50% visible            (≈ 50 vh)
export const SNAP_PEEK = 75;  // translateY(75%) → 25% visible            (≈ 25 vh)
export const SNAP_DISMISS_THRESHOLD = 90;

export type SnapPoint = typeof SNAP_FULL | typeof SNAP_MID | typeof SNAP_PEEK;
export type SnapState = 'full' | 'mid' | 'peek';

export const SNAP_POINTS: readonly SnapPoint[] = [SNAP_FULL, SNAP_MID, SNAP_PEEK];

export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.max(min, Math.min(max, value));
}

/**
 * Returns the snap point closest to the given translateY percentage.
 * Defensive against NaN/Infinity: falls back to the peek snap point.
 */
export function snapToNearest(
  currentPercent: number,
  snapPoints: readonly number[] = SNAP_POINTS,
): number {
  if (!Number.isFinite(currentPercent) || snapPoints.length === 0) {
    return SNAP_PEEK;
  }
  let nearest = snapPoints[0];
  let minDistance = Math.abs(currentPercent - nearest);
  for (const point of snapPoints) {
    const d = Math.abs(currentPercent - point);
    if (d < minDistance) {
      minDistance = d;
      nearest = point;
    }
  }
  return nearest;
}

/**
 * Swipe-down-past-peek dismiss check. Triggers when the user drags
 * the sheet below the peek snap, indicating intent to fully close.
 */
export function shouldDismiss(
  currentPercent: number,
  threshold: number = SNAP_DISMISS_THRESHOLD,
): boolean {
  if (!Number.isFinite(currentPercent)) return false;
  return currentPercent > threshold;
}

export function snapStateFor(snapPoint: number): SnapState {
  if (snapPoint <= SNAP_FULL) return 'full';
  if (snapPoint <= (SNAP_FULL + SNAP_MID) / 2) return 'full';
  if (snapPoint <= (SNAP_MID + SNAP_PEEK) / 2) return 'mid';
  return 'peek';
}
